<template>
  <DataCard
    title="Adresses"
    icon="fas fa-map-marker-alt"
    icon-color="text-green-600"
    icon-bg-color="bg-green-100"
    class="w-full"
  >
    <template #header-actions>
      <button
        @click="$emit('save')"
        :disabled="saving || disableSave"
        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        <i v-if="saving" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-save"></i>
        <span>{{ saving ? 'Sauvegarde...' : 'Sauvegarder' }}</span>
      </button>
    </template>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <!-- Adresse du magasin -->
      <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <i class="fas fa-store text-black text-sm"></i>
          </div>
          <h3 class="text-lg font-semibold text-black">Adresse du magasin</h3>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <BaseInput
              :model-value="formData.storeAddress.storeName"
              @update:model-value="updateFormData('storeAddress.storeName', $event)"
              label="Nom de l'enseigne"
              type="text"
              placeholder="Nom de votre enseigne"
              required
            />
            <!-- Autocomplétion d'adresse -->
            <div class="md:col-span-2 mb-6">
              <AddressAutocompleteField
                :model-value="formData.storeAddress.street"
                :postal-code="formData.storeAddress.postalCode"
                :city="formData.storeAddress.city"
                @update:model-value="(value: string) => updateFormData('storeAddress.street', value)"
                @update:postal-code="(value: string) => updateFormData('storeAddress.postalCode', value)"
                @update:city="(value: string) => updateFormData('storeAddress.city', value)"
                @address-selected="handleStoreAddressSelected"
              />
            </div>
            
            <!-- Champs complémentaires -->
            <BaseInput
              :model-value="formData.storeAddress.complement"
              @update:model-value="updateFormData('storeAddress.complement', $event)"
              label="Complément d'adresse"
              type="text"
              placeholder="Complément"
            />
            <BaseInput
              :model-value="formData.storeAddress.center"
              @update:model-value="updateFormData('storeAddress.center', $event)"
              label="Centre"
              type="select"
              :options="centerOptions"
            />
            <BaseInput
              :model-value="formData.storeAddress.building"
              @update:model-value="updateFormData('storeAddress.building', $event)"
              label="N° Bâtiment"
              type="text"
              placeholder="N° Bâtiment"
            />
            <BaseInput
              :model-value="formData.storeAddress.lot"
              @update:model-value="updateFormData('storeAddress.lot', $event)"
              label="Lot"
              type="text"
              placeholder="Lot"
            />
          <div class="relative">
            <BaseInput
              :model-value="formData.storeAddress.phone"
              @update:model-value="updateFormData('storeAddress.phone', $event)"
              label="Téléphone"
              type="tel"
              placeholder="Téléphone"
              required
              :class="[
                'transition-colors',
                phoneError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              ]"
            />
            <div v-if="phoneError" class="mt-1 text-xs text-red-600">
              Format téléphone invalide. Exemple : 0123456789 ou +33123456789
            </div>
          </div>
          <div class="relative">
            <BaseInput
              :model-value="formData.storeAddress.mobile"
              @update:model-value="updateFormData('storeAddress.mobile', $event)"
              label="Mobile"
              type="tel"
              placeholder="Mobile"
              required
              :class="[
                'transition-colors',
                mobileError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              ]"
            />
            <div v-if="mobileError" class="mt-1 text-xs text-red-600">
              Format mobile invalide. Exemple : 0123456789 ou +33123456789
            </div>
          </div>
        </div>
      </div>

      <!-- Adresse de facturation -->
      <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
              <i class="fas fa-file-invoice text-black text-sm"></i>
            </div>
            <h3 class="text-lg font-semibold text-black">Adresse de facturation</h3>
          </div>
          <!-- Adresse identique alignée avec le titre, sans bordure ni ombre -->
          <label for="sameAddress" class="inline-flex items-center gap-2 text-sm font-medium text-black">
            <input
              :checked="formData.billingAddress.sameAsStore"
              @change="handleSameAsStoreChange"
              type="checkbox"
              id="sameAddress"
              class="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-gray-500"
            />
            <span>Adresse identique</span>
          </label>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BaseInput
            :model-value="formData.billingAddress.company"
            @update:model-value="updateFormData('billingAddress.company', $event)"
            label="Société"
            type="text"
            placeholder="Nom de la société"
            required
          />
          <!-- Autocomplétion d'adresse de facturation -->
          <div class="md:col-span-2 mb-6">
            <AddressAutocompleteField
              :model-value="formData.billingAddress.street"
              :postal-code="formData.billingAddress.postalCode"
              :city="formData.billingAddress.city"
              @update:model-value="(value: string) => updateFormData('billingAddress.street', value)"
              @update:postal-code="(value: string) => updateFormData('billingAddress.postalCode', value)"
              @update:city="(value: string) => updateFormData('billingAddress.city', value)"
              @address-selected="handleBillingAddressSelected"
              :disabled="formData.billingAddress.sameAsStore"
              :class="{ 'opacity-50': formData.billingAddress.sameAsStore }"
            />
          </div>
          
          <div class="relative">
            <BaseInput
              :model-value="formData.billingAddress.siret"
              @update:model-value="updateFormData('billingAddress.siret', $event)"
              label="SIRET"
              type="text"
              placeholder="Numéro SIRET"
              :disabled="!canEditSiretTva"
              :class="{ 'opacity-50': !canEditSiretTva }"
            />
            <div v-if="!canEditSiretTva" class="mt-1 text-xs text-black">
              Pour modifier le SIRET, contactez le service client
            </div>
          </div>
          <div class="relative">
            <BaseInput
              :model-value="formData.billingAddress.tva"
              @update:model-value="updateFormData('billingAddress.tva', $event)"
              label="N° TVA"
              type="text"
              placeholder="Numéro TVA"
              :disabled="!canEditSiretTva"
              :class="{ 'opacity-50': !canEditSiretTva }"
            />
            <div v-if="!canEditSiretTva" class="mt-1 text-xs text-black">
              Pour modifier le numéro TVA, contactez le service client
            </div>
          </div>
          <!-- EORI (numéro d'identification douanière) -->
          <div class="relative md:col-span-2">
            <label class="block text-sm font-medium text-black mb-2">
              Numéro EORI
              <span class="text-black text-xs ml-2">(optionnel)</span>
            </label>
            <div class="relative">
              <input
                type="text"
                placeholder="FR123456789012"
                :value="eoriValue"
                @input="handleEoriInput"
                @blur="validateEori"
                :maxlength="17"
                class="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                :class="eoriHasError ? 'border-red-500 bg-red-50' : eoriIsValid && eoriValue ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'"
              />
              
              <!-- Message d'erreur -->
              <p v-if="eoriHasError" class="text-sm text-red-500 mt-2 flex items-center">
                <i class="fas fa-exclamation-circle mr-2"></i>
                {{ eoriErrorMessage }}
              </p>
              
              <!-- Message de succès -->
              <p v-else-if="eoriIsValid && eoriValue" class="text-sm text-green-600 mt-2 flex items-center">
                <i class="fas fa-check-circle mr-2"></i>
                Format EORI valide
              </p>
              
              <!-- Aide contextuelle -->
              <p v-else class="text-sm text-black mt-2">
                <i class="fas fa-info-circle mr-2"></i>
                Numéro d'identification douanière (obligatoire pour les expéditions hors UE)
              </p>
            </div>
          </div>
          <div class="relative">
            <BaseInput
              :model-value="formData.billingAddress.phone"
              @update:model-value="updateFormData('billingAddress.phone', $event)"
              label="Téléphone"
              type="tel"
              placeholder="Téléphone"
              :disabled="formData.billingAddress.sameAsStore"
              :required="!formData.billingAddress.sameAsStore"
              :class="[
                'transition-colors',
                phoneBillingError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '',
                formData.billingAddress.sameAsStore ? 'opacity-50' : ''
              ]"
            />
            <div v-if="phoneBillingError" class="mt-1 text-xs text-red-600">
              Format téléphone invalide. Exemple : 0123456789 ou +33123456789
            </div>
            <div v-else-if="formData.billingAddress.phone && !phoneBillingError" class="mt-1 text-xs text-black">
              Format : 0123456789 ou +33123456789
            </div>
          </div>
        </div>
        
      </div>
    </div>
  </DataCard>
</template>

<script setup lang="ts">
import { DataCard, BaseInput, AddressAutocompleteField } from '@/components'
import { centerOptions } from '@/data'
import { computed, onMounted, watch, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useAdminEfpStore } from '@/stores/adminEfp'

import type { GeneralInformationFormData } from '@/types'

const props = defineProps<{
  formData: GeneralInformationFormData
  saving?: boolean
  disableSave?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:formData', value: GeneralInformationFormData): void
  (e: 'validation-changed', hasErrors: boolean): void
  (e: 'save'): void
}>()

const authStore = useAuthStore()
const adminEfp = useAdminEfpStore()

const canEditSiretTva = computed(
  () => authStore.isImpersonating && adminEfp.canAccess('edit-siret-tva'),
)

onMounted(() => {
  if (authStore.isImpersonating && !adminEfp.isAdminEfpValidated) {
    void adminEfp.validate()
  }
})

// Regex de validation
const phoneRegex = /^(0[1-9](\d{8})|\+33[1-9](\d{8}))$/

// États de validation
const phoneError = computed(() => {
  if (!props.formData.storeAddress.phone) return false
  return !phoneRegex.test(props.formData.storeAddress.phone)
})

const mobileError = computed(() => {
  if (!props.formData.storeAddress.mobile) return false
  return !phoneRegex.test(props.formData.storeAddress.mobile)
})

const phoneBillingError = computed(() => {
  if (!props.formData.billingAddress.phone) return false
  return !phoneRegex.test(props.formData.billingAddress.phone)
})

// État local pour EORI
const eoriValue = ref(props.formData.billingAddress.eori || '')
const eoriHasError = ref(false)
const eoriIsValid = ref(false)
const eoriErrorMessage = ref('')

// Gestion EORI avec blocage automatique
const handleEoriInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value
  
  const cleanValue = value.replace(/\s/g, '')
  
  if (cleanValue.length > 17) {
    return // Bloque si on dépasse 17 caractères
  }
  
  // Blocage strict : si on a exactement 17 caractères, on ne peut plus taper
  if (cleanValue.length >= 17) {
    const finalValue = cleanValue.substring(0, 17).toUpperCase()
    eoriValue.value = finalValue
    updateFormData('billingAddress.eori', finalValue)
    validateEori()
    return
  }
  
  eoriValue.value = value.toUpperCase()
  updateFormData('billingAddress.eori', value.toUpperCase())
  validateEori()
}

// Validation EORI
// Format EORI : 2 lettres (code pays) + jusqu'à 15 caractères alphanumériques (max 17 caractères total)
const validateEori = () => {
  const value = eoriValue.value.trim()
  
  if (!value) {
    eoriHasError.value = false
    eoriIsValid.value = false
    eoriErrorMessage.value = ''
    return
  }
  
  const cleanValue = value.replace(/\s/g, '').toUpperCase()
  // Format EORI : 2 lettres (code pays ISO) + 1 à 15 caractères alphanumériques
  const eoriRegex = /^[A-Z]{2}[A-Z0-9]{1,15}$/
  
  if (cleanValue.length < 3 || cleanValue.length > 17) {
    eoriHasError.value = true
    eoriIsValid.value = false
    eoriErrorMessage.value = 'Format EORI invalide. Exemple : FR123456789012'
    return
  }
  
  if (!eoriRegex.test(cleanValue)) {
    eoriHasError.value = true
    eoriIsValid.value = false
    eoriErrorMessage.value = 'Format EORI invalide. Exemple : FR123456789012'
    return
  }
  
  eoriHasError.value = false
  eoriIsValid.value = true
  eoriErrorMessage.value = ''
}

// Validation globale - détecte toutes les erreurs
const hasValidationErrors = computed(() => {
  // Erreurs de format téléphones
  const phoneFormatErrors = phoneError.value || mobileError.value || phoneBillingError.value
  
  // Erreurs de téléphones obligatoires
  const phoneRequiredErrors = !props.formData.storeAddress.phone || 
                             !props.formData.storeAddress.mobile ||
                             (!props.formData.billingAddress.sameAsStore && !props.formData.billingAddress.phone)
  
  // Erreurs de champs requis pour l'adresse du magasin
  const requiredStoreFields = !props.formData.storeAddress.storeName || 
                              !props.formData.storeAddress.street || 
                              !props.formData.storeAddress.city || 
                              !props.formData.storeAddress.postalCode
  
  // Erreurs de champs requis pour l'adresse de facturation (si pas "même adresse")
  const requiredBillingFields = !props.formData.billingAddress.sameAsStore && 
                               (!props.formData.billingAddress.company ||
                                !props.formData.billingAddress.street ||
                                !props.formData.billingAddress.city ||
                                !props.formData.billingAddress.postalCode)
  
  // Société toujours obligatoire même si "même adresse"
  const companyRequired = !props.formData.billingAddress.company
  
  return !!(phoneFormatErrors || phoneRequiredErrors || requiredStoreFields || requiredBillingFields || companyRequired)
})

const updateFormData = (field: string, value: any) => {
  const keys = field.split('.')

  const updatedFormData = {
    ...props.formData,
    [keys[0]]: { ...(props.formData as any)[keys[0]] }
  }

  let current: any = updatedFormData
  for (let i = 0; i < keys.length - 1; i++) {
    current = current[keys[i]]
  }

  current[keys[keys.length - 1]] = value
  emit('update:formData', updatedFormData)
}



// Utilitaire: copie l'adresse du magasin vers la facturation
// (évite d'émettre si aucune donnée ne change pour prévenir les boucles)
const copyStoreToBilling = () => {
  const s = props.formData.storeAddress
  const b = props.formData.billingAddress

  const noChange =
    b.number === s.number &&
    b.street === s.street &&
    b.city === s.city &&
    b.postalCode === s.postalCode &&
    b.phone === s.phone

  if (noChange) return

  // émettre uniquement si une valeur diffère
  const updatedFormData = {
    ...props.formData,
    billingAddress: {
      ...props.formData.billingAddress,
      number: s.number,
      street: s.street,
      city: s.city,
      postalCode: s.postalCode,
      phone: s.phone,
    },
  }
  emit('update:formData', updatedFormData)
}

const handleSameAsStoreChange = (event: Event) => {
  const isChecked = (event.target as HTMLInputElement).checked
  
  // Mettre à jour la checkbox
  updateFormData('billingAddress.sameAsStore', isChecked)
  
  // Si coché, copier les valeurs de l'adresse du magasin
  if (isChecked) {
    copyStoreToBilling()
  }
}

const handleStoreAddressSelected = (address: { streetAddress: string; postalCode: string; city: string; fullAddress: string }) => {
  const updatedFormData = { ...props.formData }
  updatedFormData.storeAddress.street = address.streetAddress
  updatedFormData.storeAddress.postalCode = address.postalCode
  updatedFormData.storeAddress.city = address.city
  emit('update:formData', updatedFormData)
}

const handleBillingAddressSelected = (address: { streetAddress: string; postalCode: string; city: string; fullAddress: string }) => {
  const updatedFormData = { ...props.formData }
  updatedFormData.billingAddress.street = address.streetAddress
  updatedFormData.billingAddress.postalCode = address.postalCode
  updatedFormData.billingAddress.city = address.city
  emit('update:formData', updatedFormData)
}

// Watcher pour émettre les changements de validation
watch(hasValidationErrors, (hasErrors) => {
  emit('validation-changed', hasErrors)
}, { immediate: true })

// Synchroniser automatiquement l'adresse de facturation quand "Adresse identique" est cochée
// Utilise des sources multiples pour éviter de recréer un objet et déclencher des mises à jour inutiles
watch(
  [
    () => props.formData.billingAddress.sameAsStore,
    () => props.formData.storeAddress.street,
    () => props.formData.storeAddress.city,
    () => props.formData.storeAddress.postalCode,
    () => props.formData.storeAddress.phone,
    () => props.formData.storeAddress.number,
  ],
  (newVals) => {
    const same = newVals[0] as boolean
    if (same) {
      copyStoreToBilling()
    }
  }
)

// Synchronisation avec les props pour EORI
watch(() => props.formData.billingAddress.eori, (newValue) => {
  eoriValue.value = newValue || ''
  if (newValue) {
    validateEori()
  }
}, { immediate: true })
</script>
